import React, { Fragment, useRef, useState, useMemo, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { Listbox, Transition } from "@headlessui/react";
import { ChevronDownIcon, ChevronUpIcon, CheckIcon } from "@heroicons/react/20/solid";

interface Option {
  value: string | number;
  label: string;
  subLabel?: string;
  isPlaceholder?: boolean;
  /**
   * When true, renders this option's label in bold to mirror the legacy
   * portal's emphasised system/seeded entries (e.g. "All Available Fields"
   * and provider-rate-derived export profiles). Pure display flag — does
   * not affect value selection.
   */
  isBold?: boolean;
  /**
   * Optional inline node rendered immediately before the label. Used to
   * surface a non-interactive indicator (e.g. a 📌 pin marking the
   * default dashboard) inside the dropdown options AND in the
   * selected-value button face. Pure display — does not affect
   * selection, search, or keyboard navigation.
   */
  prefix?: React.ReactNode;
}

interface CustomSelectProps {
  label?: string | React.ReactNode;
  /** Plain-string title for the label element tooltip. Required when `label` is JSX (not a string). */
  labelTitle?: string;
  options: Option[];
  value?: string | number;
  onChange?: (value: string | number) => void;
  id?: string | undefined;
  className?: string | undefined;
  disabled?: boolean | undefined;
  "data-testid"?: string | undefined;
  direction?: 'up' | 'down';
  placeholder?: string;
  buttonClassName?: string;
  searchable?: boolean;
  title?: string | undefined;
  /**
   * Accessible label for the listbox button — used when `label` is empty/absent so screen readers
   * can still announce the field name. Only applied when `aria-labelledby` is absent; per W3C ARIA
   * spec, `aria-labelledby` always takes precedence. When used, the synthetic format is
   * "{aria-label} dropdown, {selected value}" — the value is included because an explicit
   * aria-label replaces the button's visible text in accessible-name computation, so the
   * selection would otherwise never be announced at all.
   * Must be a plain string — do not pass JSX or markup. Sanitized with String().trim() internally.
   */
  "aria-label"?: string | undefined;
  /**
   * ID of an external label element to associate with the listbox button via aria-labelledby.
   * Per W3C ARIA spec, takes precedence over `aria-label` when both are provided. Use this
   * when a visible `<label>` element with the given id exists in the DOM (e.g. FilterShell's
   * rendered `<label id="…">`). When set, `aria-label` is suppressed on Listbox.Button.
   */
  "aria-labelledby"?: string | undefined;
  /** Indicates invalid field state for screen readers (e.g. "true" when validation fails). */
  "aria-invalid"?: boolean | "false" | "true" | "grammar" | "spelling" | undefined;
  /** Marks field as required for screen readers. */
  "aria-required"?: boolean | undefined;
  /** ID(s) of element(s) that describe this field (e.g. error message element id). */
  "aria-describedby"?: string | undefined;
  /** When true, shows the selected value as tooltip on the button and option labels as tooltips on each item. Defaults to true. */
  showOptionTooltips?: boolean;
  dropdownClassName?: string;
  /** Optional: called when the user scrolls near the bottom of the dropdown list */
  onLoadMore?: () => void;
  /** Whether more items are available to load */
  hasMore?: boolean;
  /** Whether more items are currently being loaded */
  isLoadingMore?: boolean;
  /** Optional: called when the dropdown opens — useful for lazy-loading initial data */
  onOpen?: () => void;
  /** Optional: called when the search input changes — enables server-side search */
  onSearchChange?: (query: string) => void;
  /**
   * QA #2421 / #2423 / #2425 — when provided, returns a URL for an option so
   * that Cmd/Ctrl/middle-click opens the entity in a new tab while plain
   * left-click still selects the option (mirrors the
   * ServerSideSearchableSelect single-select linkItems pattern). Return
   * undefined/null for options that shouldn't link (placeholders, "Any"
   * sentinels, etc.).
   */
  getItemUrl?: (option: Option) => string | undefined | null;
}

const OpenStateSync: React.FC<{
  open: boolean;
  onChange: (open: boolean) => void;
}> = ({ open, onChange }) => {
  // useLayoutEffect so that isOpen state is updated synchronously after DOM
  // mutation and before the browser paints. This ensures the position
  // calculation useLayoutEffect below always runs with up-to-date isOpen.
  useLayoutEffect(() => {
    onChange(open);
  }, [open, onChange]);

  return null;
};

/**
 * A11y fix for HeadlessUI v1.7 Listbox + createPortal pattern (NON-searchable selects only).
 *
 * Problem: HeadlessUI keeps focus on Listbox.Button while the dropdown is open,
 * but places `aria-activedescendant` on the <ul role="listbox"> which is never
 * focused. Screen readers follow `aria-activedescendant` only on the FOCUSED
 * element, so keyboard-navigated options are announced as blank.
 *
 * Additionally, the <ul> is rendered via createPortal into document.body, so
 * it is not a DOM descendant of the button — `aria-controls` alone is
 * insufficient for some screen readers to establish the ownership relationship.
 *
 * Fix:
 * 1. Mirror `aria-activedescendant` from the <ul> to the focused <button> via
 *    a MutationObserver, so arrow-key navigation is announced correctly.
 * 2. Set `aria-owns` on the <button> pointing to the portalled <ul>'s id, so
 *    screen readers treat the listbox as logically owned by the button.
 * 3. Clear both attributes when the dropdown closes.
 *
 * NOTE: For searchable selects, focus moves to the search <input> which has its
 * own combobox ARIA semantics and aria-activedescendant managed via React state
 * (see activeSearchIndex in CustomSelect). This component is NOT used there.
 */
const ActiveDescendantSync: React.FC<{
  isOpen: boolean;
  buttonRef: React.RefObject<HTMLButtonElement>;
}> = ({ isOpen, buttonRef }) => {
  useEffect(() => {
    const button = buttonRef.current;
    if (!button || !isOpen) return;

    let observer: MutationObserver | null = null;

    // Defer one animation frame so that HeadlessUI's Listbox.Options has fully
    // mounted and aria-controls on the button has been populated.
    const rafId = requestAnimationFrame(() => {
      if (!button) return;

      // HeadlessUI sets aria-controls on the button pointing to the portalled
      // <ul role="listbox"> id. Use this to locate the options list element.
      const optionsListId = button.getAttribute('aria-controls');
      if (!optionsListId) return;

      const ul = document.getElementById(optionsListId);
      if (!ul) return;

      // Set aria-owns so screen readers treat the portalled <ul> as logically
      // owned by the focused <button>, even though it is a DOM sibling at <body>.
      button.setAttribute('aria-owns', optionsListId);

      // Sync aria-activedescendant from <ul> → <button> so that the focused
      // <button> always carries the currently-highlighted option's id.
      // Screen readers follow aria-activedescendant only on the focused element.
      const syncActiveDescendant = () => {
        const activeId = ul.getAttribute('aria-activedescendant');
        if (activeId) {
          button.setAttribute('aria-activedescendant', activeId);
        } else {
          button.removeAttribute('aria-activedescendant');
        }
      };

      // Run once immediately (in case an option is already active on open)
      syncActiveDescendant();

      observer = new MutationObserver(syncActiveDescendant);
      observer.observe(ul, { attributes: true, attributeFilter: ['aria-activedescendant'] });
    });

    return () => {
      cancelAnimationFrame(rafId);
      observer?.disconnect();
      button.removeAttribute('aria-activedescendant');
      button.removeAttribute('aria-owns');
    };
  }, [isOpen, buttonRef]);

  return null;
};

export const CustomSelect: React.FC<CustomSelectProps> = ({
  label,
  labelTitle,
  options,
  value,
  onChange,
  id,
  className = "",
  disabled = false,
  direction = 'down',
  placeholder = "Select...",
  buttonClassName = "",
  searchable = false,
  showOptionTooltips = false,
  dropdownClassName = "max-h-60 overflow-auto",
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
  onOpen,
  onSearchChange,
  title,
  getItemUrl,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledby,
  "aria-invalid": ariaInvalid,
  "aria-required": ariaRequired,
  "aria-describedby": ariaDescribedby,
  ...props
}) => {
  const listboxButtonRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  // Keyboard navigation state for searchable selects.
  // Tracks which filtered-option index is currently highlighted when the user
  // presses arrow keys while focus is in the search <input>. We manage this
  // ourselves because HeadlessUI's keyboard handler requires focus on the
  // Listbox.Button, but searchable selects move focus to the search input.
  const [activeSearchIndex, setActiveSearchIndex] = useState(-1);

  // Stable instance-level ID prefix so option elements get predictable ids
  // for aria-activedescendant without collisions across multiple CustomSelect
  // instances on the same page. Using a ref (not useId) avoids React 18
  // version requirement and keeps the value stable across renders.
  const instanceIdRef = useRef(`cs-${Math.random().toString(36).slice(2, 8)}`);

  useEffect(() => {
    if (isOpen && searchable) {
      const focusTimeout = setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus({ preventScroll: true });
        }
      }, 50);
      return () => clearTimeout(focusTimeout);
    }
    return undefined;
  }, [isOpen, searchable]);

  // Reset active search index whenever the filtered list changes (user typed)
  // or when the dropdown closes, so the next open always starts at -1.
  useEffect(() => {
    setActiveSearchIndex(-1);
  }, [searchTerm]);

  useEffect(() => {
    if (!isOpen) setActiveSearchIndex(-1);
  }, [isOpen]);

  // Compute dropdown position synchronously at render time directly from the
  // button's bounding rect. This avoids the two-render-cycle delay of using
  // state (open → setIsOpen → re-render → useEffect → setDropdownStyle →
  // re-render) which caused a brief frame where the portal rendered with no
  // position styles, shifting the document layout and flashing the scrollbar.
  //
  // When `direction='down'` (default) but the trigger sits near the bottom of
  // the viewport, the dropdown panel (~max-h-60 ≈ 240px) gets clipped below
  // the fold. Auto-flip upward when there's not enough room below AND there's
  // strictly more room above. Filter side-panels with the last filter near
  // the bottom (e.g. Archive Schedules → Other → Dispatch type) were the
  // original case but the heuristic helps anywhere the trigger ends up low.
  const ESTIMATED_DROPDOWN_HEIGHT_PX = 240;
  const getDropdownStyle = (): React.CSSProperties => {
    if (!listboxButtonRef.current) return {};
    const rect = listboxButtonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const shouldFlipUp =
      direction !== 'up'
      && spaceBelow < ESTIMATED_DROPDOWN_HEIGHT_PX
      && spaceAbove > spaceBelow;
    if (direction === 'up' || shouldFlipUp) {
      return {
        position: 'fixed',
        bottom: window.innerHeight - rect.top + 4,
        left: rect.left,
        width: rect.width,
        zIndex: 9999,
      };
    }
    return {
      position: 'fixed',
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      zIndex: 9999,
    };
  };

  // Close dropdown when any scrollable ancestor scrolls (keeps fixed portal aligned)
  useEffect(() => {
    if (!isOpen) return;

    // Programmatically close the headlessui Listbox by clicking the button again
    const closeOnScroll = () => {
      listboxButtonRef.current?.click();
    };

    // Walk up from the button and attach scroll listener to every scrollable ancestor
    const scrollableAncestors: EventTarget[] = [];
    let el: HTMLElement | null = listboxButtonRef.current?.parentElement ?? null;
    while (el) {
      const { overflow, overflowY } = window.getComputedStyle(el);
      if (/(auto|scroll)/.test(overflow) || /(auto|scroll)/.test(overflowY)) {
        scrollableAncestors.push(el);
      }
      el = el.parentElement;
    }
    // Always watch window scroll too
    scrollableAncestors.push(window);

    // Defer listener registration by one tick. Rendering the portal dropdown
    // into document.body triggers a layout reflow that can emit a synthetic
    // scroll event on window before the paint completes. Without the delay,
    // that spurious scroll fires closeOnScroll() and immediately closes the
    // dropdown on the very first open after a page refresh. Subsequent opens
    // are unaffected because the layout slot already exists.
    const timerId = setTimeout(() => {
      scrollableAncestors.forEach((ancestor) =>
        ancestor.addEventListener('scroll', closeOnScroll, { passive: true })
      );
    }, 0);

    return () => {
      clearTimeout(timerId);
      scrollableAncestors.forEach((ancestor) =>
        ancestor.removeEventListener('scroll', closeOnScroll)
      );
    };
  }, [isOpen]);

  // Fire onOpen only once when the dropdown transitions from closed to open
  useEffect(() => {
    if (isOpen && onOpen) {
      onOpen();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Find the selected option, or use placeholder
  const selectedOption = options.find((option) => String(option.value) === String(value));
  const currentValue = value !== undefined && value !== null ? value : '';
  // isPlaceholder is true when:
  // 1. No value is selected (nothing matches) AND no value was provided
  // 2. The selected option is explicitly marked as a placeholder
  // 3. The selected option is at index 0 AND has an empty/falsy value (convention: first option = "no filter" / "all")
  //    Note: index 0 with a real value (e.g., fare type id=0 "Cash") must NOT be treated as placeholder
  const selectedOptionIndex = selectedOption ? options.indexOf(selectedOption) : -1;
  const isPlaceholder =
    (selectedOption === undefined && (value === undefined || value === null || value === '')) ||
    selectedOption?.isPlaceholder === true ||
    (selectedOptionIndex === 0 && (selectedOption?.value === '' || selectedOption?.value === undefined || selectedOption?.value === null));
  const displayLabel = selectedOption?.label || placeholder;

  const filteredOptions = useMemo(() =>
    options.filter((option) => {
      const normalizedLabel = typeof option.label === 'string'
        ? option.label
        : String(option.label ?? '');

      return normalizedLabel.toLowerCase().includes(searchTerm.toLowerCase());
    }), [options, searchTerm]);

  // Stable id used for aria-controls / aria-owns on the search input
  const optionsListAriaId = `${instanceIdRef.current}-options`;

  // Scroll the highlighted option into view when activeSearchIndex changes
  useEffect(() => {
    if (!searchable || activeSearchIndex < 0) return;
    const optionId = `${instanceIdRef.current}-option-${String(filteredOptions[activeSearchIndex]?.value ?? '')}`;
    const el = document.getElementById(optionId);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeSearchIndex, filteredOptions, searchable]);

  return (
    <div className={className} ref={containerRef}>
      <Listbox value={currentValue} onChange={(val) => {
        if(onChange) onChange(val);
      }} disabled={disabled}>
        {({ open }) => {
          return (
            <>
              <OpenStateSync open={open} onChange={setIsOpen} />
              {/* ActiveDescendantSync only used for non-searchable selects.
                  For searchable selects, focus stays on the search <input> which
                  has its own combobox ARIA and aria-activedescendant managed via
                  activeSearchIndex state. HeadlessUI's keyboard navigation is not
                  used in the searchable path. */}
              {!searchable && (
                <ActiveDescendantSync isOpen={open} buttonRef={listboxButtonRef} />
              )}
              {/* Render a visible label when label text is provided.
                  Headless UI wires Listbox.Label → Listbox.Button via
                  aria-labelledby automatically, which is the most reliable
                  way for NVDA to announce the field name on focus.
                  NOTE (reversal of PR #2937): The sr-only Listbox.Label that PR #2937 added
                  for the ariaLabel-only path has been deliberately removed. Direct aria-label
                  on Listbox.Button is used instead, because the HeadlessUI auto-wire produced
                  double-announcements when both the sr-only Listbox.Label and an explicit
                  aria-label/aria-labelledby were present simultaneously (e.g. FilterShell
                  injecting both). This reversal was validated against NVDA, JAWS, and VoiceOver. */}
              {(label !== undefined && label !== null && label !== '') ? (
                <Listbox.Label
                  className="block text-sm font-medium text-text mb-1"
                  title={labelTitle ?? (typeof label === 'string' ? label : undefined)}
                >
                  {label}
                </Listbox.Label>
              ) : null}
              {/* Accessible name strategy (W3C ARIA spec precedence restored):
                  1. ariaLabelledby wins when provided — set directly on Listbox.Button.
                     HeadlessUI leaves explicit aria-labelledby intact.
                  2. ariaLabel is used only when ariaLabelledby is absent.
                     The synthetic "{label} dropdown" format gives SR users control-type context.
                  3. When neither is supplied, HeadlessUI's Listbox.Label auto-wire handles it. */}
              <div className="relative">
                <Listbox.Button
                  id={id}
                  ref={listboxButtonRef}
                  data-testid={props["data-testid"]}
                  title={title ?? (showOptionTooltips && !isPlaceholder ? displayLabel : undefined)}
                  // Accessible name strategy (W3C ARIA spec precedence):
                  //
                  // 1. aria-labelledby wins when ariaLabelledby is provided — references the
                  //    visible external label element (e.g. FilterShell's <label id="…">).
                  //    Per W3C ARIA spec, aria-labelledby always takes precedence over aria-label.
                  //
                  // 2. aria-label is used only when ariaLabelledby is absent. The format is
                  //    "{field label} dropdown, {selected value}". The value IS included here
                  //    (not left to the button's visible text) because an explicit aria-label
                  //    replaces content in accessible-name computation — the visible text is
                  //    never read alongside it, so omitting the value here left the current
                  //    selection completely unannounced.
                  //
                  // 3. When neither is supplied, HeadlessUI's own Listbox.Label auto-wire handles it.
                  aria-labelledby={ariaLabelledby || undefined}
                  aria-label={
                    !ariaLabelledby && ariaLabel
                      ? `${String(ariaLabel).trim()} dropdown${!isPlaceholder && displayLabel ? `, ${displayLabel}` : ""}`
                      : undefined
                  }
                  aria-invalid={ariaInvalid || undefined}
                  aria-required={ariaRequired || undefined}
                  aria-describedby={ariaDescribedby || undefined}
                  className={`relative w-full h-[36px] cursor-default rounded-[4px] border border-border bg-surface py-[6px] px-[10px] text-left text-[14px] ${isPlaceholder ? 'text-accordionArrow' : (getItemUrl && selectedOption && getItemUrl(selectedOption)) ? 'text-tableBlueColor' : 'text-text'} outline-none focus:ring-2 focus:ring-ring focus:border-ring hover:border-ring hover:bg-surface-alt transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${buttonClassName}`}
                  style={{ outlineOffset: 0 }}
                >
                  <span className="block truncate pr-8">
                    {selectedOption?.prefix ? (
                      <span className="mr-1.5 inline-flex align-middle">
                        {selectedOption.prefix}
                      </span>
                    ) : null}
                    {displayLabel}
                  </span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-[10px]">
                    {direction === 'up' && open ? (
                      <ChevronUpIcon
                        className={`h-5 w-5 ${open ? 'text-text' : 'text-accordionArrow'}`}
                        aria-hidden="true"
                      />
                    ) : (
                      <ChevronDownIcon
                        className={`h-5 w-5 ${open ? 'text-text' : 'text-accordionArrow'}`}
                        aria-hidden="true"
                      />
                    )}
                  </span>
                </Listbox.Button>

                {createPortal(
                  <Transition
                    show={open}
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                    afterLeave={() => {
                      setSearchTerm('');
                      listboxButtonRef.current?.focus();
                    }}
                  >
                    <Listbox.Options
                      static
                      id={searchable ? optionsListAriaId : undefined}
                      style={getDropdownStyle()}
                      className={`${dropdownClassName} rounded-md py-1 shadow-lg focus:outline-none bg-surface border border-border-strong`}
                      onScroll={onLoadMore ? (e: React.UIEvent<HTMLUListElement>) => {
                        const el = e.currentTarget;
                        if (!hasMore || isLoadingMore) return;
                        if (el.scrollHeight - el.scrollTop - el.clientHeight < 40) {
                          onLoadMore();
                        }
                      } : undefined}
                    >
                      {searchable && (
                        <div className="p-2">
                          <input
                            ref={searchInputRef}
                            type="text"
                            placeholder="Search..."
                            title="Search options"
                            // Combobox semantics: the search input owns and navigates the listbox.
                            // aria-activedescendant points to the currently highlighted option's id
                            // (managed via activeSearchIndex state) so screen readers announce
                            // the active option as the user presses arrow keys — without ever
                            // moving focus away from this input.
                            role="combobox"
                            aria-label="Search options"
                            aria-autocomplete="list"
                            aria-haspopup="listbox"
                            aria-expanded={open}
                            aria-controls={optionsListAriaId}
                            aria-owns={optionsListAriaId}
                            aria-activedescendant={
                              activeSearchIndex >= 0 && filteredOptions[activeSearchIndex]
                                ? `${instanceIdRef.current}-option-${String(filteredOptions[activeSearchIndex].value)}`
                                : undefined
                            }
                            className="w-full px-3 py-2 border border-input rounded-md bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary"
                            value={searchTerm}
                            onChange={(e) => {
                              setSearchTerm(e.target.value);
                              if (onSearchChange) onSearchChange(e.target.value);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                              // Handle keyboard navigation entirely in React state.
                              // Focus stays on this input; aria-activedescendant on
                              // this input is updated to point to the highlighted option,
                              // causing screen readers to announce it without any focus move.
                              if (e.key === 'ArrowDown') {
                                e.preventDefault();
                                setActiveSearchIndex((prev) =>
                                  prev < filteredOptions.length - 1 ? prev + 1 : prev
                                );
                              } else if (e.key === 'ArrowUp') {
                                e.preventDefault();
                                setActiveSearchIndex((prev) => (prev > 0 ? prev - 1 : 0));
                              } else if (e.key === 'Enter') {
                                e.preventDefault();
                                if (activeSearchIndex >= 0 && filteredOptions[activeSearchIndex]) {
                                  if (onChange) onChange(filteredOptions[activeSearchIndex].value);
                                  listboxButtonRef.current?.click();
                                }
                              } else if (e.key === 'Escape') {
                                e.preventDefault();
                                listboxButtonRef.current?.click();
                              }
                            }}
                          />
                        </div>
                      )}
                      {(searchable ? filteredOptions : options).map((option, optionIndex) => {
                        // QA #2421/#2423/#2425 — when getItemUrl returns a URL,
                        // Cmd/Ctrl/middle-click opens that URL in a new tab.
                        // Plain click selects the option (Headless UI's
                        // Listbox.Option default behaviour). To make plain
                        // click reliably select on browsers where an inner
                        // <a href> swallows the click event, we intercept
                        // modifier-key clicks at MOUSEDOWN (before Listbox's
                        // own click handler fires), open the URL via
                        // window.open, and stop propagation. Plain clicks fall
                        // through to Listbox unmolested.
                        const optionUrl = getItemUrl ? getItemUrl(option) : undefined;
                        const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
                          if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) {
                            e.stopPropagation();
                            // Let the browser open the link in a new tab via the
                            // native <a href> — do NOT select the option.
                            return;
                          }
                          e.preventDefault();
                          e.stopPropagation();
                          if (onChange) onChange(option.value);
                          listboxButtonRef.current?.click();
                        };
                        // For searchable mode: highlight is driven by our own activeSearchIndex
                        // state (not HeadlessUI's active state from pointer/keyboard on button).
                        const isSearchHighlighted = searchable && optionIndex === activeSearchIndex;
                        const isOptionSelected = String(option.value) === String(value);
                        return (
                        <Listbox.Option
                         key={option.value}
                         value={option.value}
                         // Stable id so the search input's aria-activedescendant can
                         // reference this element. Only set in searchable mode.
                         id={searchable ? `${instanceIdRef.current}-option-${String(option.value)}` : undefined}
                        // aria-label={option.subLabel ? `${option.label} ${option.subLabel}` : option.label}
                         // aria-label provides the accessible name for each option.
                         // For the selected item: embed "selected" so AT announce it
                         // (some AT omit aria-selected="true" for already-selected items).
                         // For unselected items: use name only — AT append "not selected"
                         // from aria-selected="false", avoiding double-announcement.
                         aria-label={isOptionSelected
                           ? `${option.label}${option.subLabel ? `, ${option.subLabel}` : ''}, selected`
                           : `${option.label}${option.subLabel ? `, ${option.subLabel}` : ''}`}
                         className={({ active, selected }) =>
                            `group relative cursor-default select-none py-2 pl-3 mr-1 pr-9 rounded-[4px] ml-1 ${
                              selected
                                ? "bg-primary text-surface"
                                : (isSearchHighlighted || active)
                                  ? "bg-surface-alt text-text"
                                  : option.isPlaceholder
                                    ? "text-accordionArrow"
                                    : optionUrl
                                      ? "text-tableBlueColor"
                                      : "text-text"
                            }`
                          }
                        >
                          {({ selected, active }) => {
                            const labelEl = (
                              <span
                                title={showOptionTooltips ? option.label : undefined}
                                className={`block text-[0.85rem] ${
                                  selected ? "font-medium" : option.isBold ? "font-bold" : "font-normal"
                                } ${!active && !selected && option.isPlaceholder ? "text-text-muted" : ""}`}
                              >
                                {option.prefix ? (
                                  <span className="mr-1.5 inline-flex align-middle">
                                    {option.prefix}
                                  </span>
                                ) : null}
                                {option.label}
                              </span>
                            );
                            const subLabelEl = option.subLabel ? (
                              <span className={`block text-[0.75rem] mt-0.5 ${selected ? "text-surface/80" : "text-text-muted"}`}>
                                {option.subLabel}
                              </span>
                            ) : null;
                            const checkEl = selected ? (
                              <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-surface">
                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                              </span>
                            ) : null;
                            return (
                              <>
                                {optionUrl ? (
                                  <a
                                    href={optionUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={handleAnchorClick}
                                    className="block"
                                    tabIndex={-1}
                                  >
                                    {labelEl}
                                    {subLabelEl}
                                  </a>
                                ) : (
                                  <>
                                    {labelEl}
                                    {subLabelEl}
                                  </>
                                )}
                                {checkEl}
                              </>
                            );
                          }}
                        </Listbox.Option>
                      );})}
                      {isLoadingMore && (
                        <div className="px-3 py-2 text-sm text-text-muted text-center">Loading...</div>
                      )}
                      {!isLoadingMore && hasMore && (
                        <div className="px-3 py-2 text-xs text-text-muted text-center select-none">Scroll for more</div>
                      )}
                    </Listbox.Options>
                  </Transition>,
                  document.body
                )}
              </div>
            </>
          );
        }}
      </Listbox>
    </div>
  );
};
